import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { CoreMembership, CoreOrganization, UserLabel } from '@/lib/zifekCore';
import { listAuditLog, type CoreAuditEntry, auditActionMeta } from '@/lib/coreAudit';

interface Props {
  org: CoreOrganization;
  users: Record<number, UserLabel>;
  memberships?: CoreMembership[];
}

interface TimelineRow {
  id: number;
  entity_type: string | null;
  event_type: string | null;
  title: string | null;
  description: string | null;
  actor_user_id: number | null;
  actor_name: string | null;
  icon: string | null;
  occurred_at: string | null;
  created_at: string | null;
}

type Kind = 'create' | 'update' | 'delete' | 'payment' | 'other';

interface LogEntry {
  key: string;
  at: string;
  kind: Kind;
  icon: string;
  className: string;
  actor: string;
  actorUserId: number | null;
  title: string;
  detail: string | null;
}

const KIND_META: Record<Kind, { label: string; icon: string; className: string }> = {
  create: { label: 'Création', icon: 'ri-add-circle-line', className: 'bg-accent-100 text-accent-900' },
  update: { label: 'Modification', icon: 'ri-edit-line', className: 'bg-secondary-100 text-secondary-900' },
  delete: { label: 'Suppression', icon: 'ri-delete-bin-line', className: 'bg-red-100 text-red-700' },
  payment: { label: 'Paiement', icon: 'ri-money-dollar-circle-line', className: 'bg-primary-100 text-primary-900' },
  other: { label: 'Activité', icon: 'ri-information-line', className: 'bg-background-100 text-foreground-600' },
};

function timelineKind(eventType: string | null): Kind {
  const t = (eventType || '').toLowerCase();
  if (t.includes('delete') || t.includes('archiv')) return 'delete';
  if (t.includes('created') || t === 'create') return 'create';
  if (t.includes('payment') || t.includes('paid')) return 'payment';
  if (t.includes('status') || t.includes('updated')) return 'update';
  return 'other';
}

function auditKind(action: string): Kind {
  const base = (action || '').split('.')[0];
  if (base === 'delete') return 'delete';
  if (base === 'create') return 'create';
  if (base === 'payment') return 'payment';
  if (base === 'update') return 'update';
  return 'other';
}

function humanize(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function csvEscape(value: string | null | undefined): string {
  const s = String(value ?? '');
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function dayStartIso(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}

function dayEndIso(date: string): string {
  return new Date(`${date}T23:59:59`).toISOString();
}

export default function ActivityLogTab({ org, users, memberships }: Props) {
  const [timeline, setTimeline] = useState<TimelineRow[]>([]);
  const [audit, setAudit] = useState<CoreAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | Kind>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [workerId, setWorkerId] = useState<'all' | number>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tl, au] = await Promise.all([
        supabase
          .from('core_activity_timeline')
          .select('id, entity_type, event_type, title, description, actor_user_id, actor_name, icon, occurred_at, created_at')
          .eq('organization_id', org.id)
          .order('occurred_at', { ascending: false })
          .limit(200),
        listAuditLog(org.id, { limit: 200 }),
      ]);
      setTimeline((tl.data || []) as TimelineRow[]);
      setAudit(au);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le journal d’activité.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const actorLabel = useCallback((userId: number | null, fallback: string | null) => {
    if (userId != null && users[userId]?.name) return users[userId].name as string;
    return fallback || 'Système';
  }, [users]);

  const entries = useMemo<LogEntry[]>(() => {
    const fromTimeline: LogEntry[] = timeline.map((r) => {
      const kind = timelineKind(r.event_type);
      return {
        key: `tl-${r.id}`,
        at: r.occurred_at || r.created_at || '',
        kind,
        icon: r.icon || KIND_META[kind].icon,
        className: KIND_META[kind].className,
        actor: actorLabel(r.actor_user_id, r.actor_name),
        actorUserId: r.actor_user_id,
        title: r.title || humanize(r.event_type) || 'Activité',
        detail: r.description || (r.entity_type ? `Module : ${humanize(r.entity_type)}` : null),
      };
    });
    const fromAudit: LogEntry[] = audit.map((r) => {
      const kind = auditKind(r.action);
      const meta = auditActionMeta(r.action);
      return {
        key: `au-${r.id}`,
        at: r.created_at || '',
        kind,
        icon: meta.icon || KIND_META[kind].icon,
        className: KIND_META[kind].className,
        actor: actorLabel(r.user_id, r.user_name),
        actorUserId: r.user_id,
        title: `${humanize(r.action) || 'Action'}${r.resource_type ? ` — ${humanize(r.resource_type)}` : ''}`,
        detail: r.resource_id ? `Réf. #${r.resource_id}` : null,
      };
    });
    return [...fromTimeline, ...fromAudit].sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [timeline, audit, actorLabel]);

  // Options travailleur : depuis les memberships (préféré), sinon depuis les libellés connus.
  const workerOptions = useMemo(() => {
    const ids = new Set<number>();
    if (memberships && memberships.length > 0) {
      memberships.forEach((m) => ids.add(m.user_id));
    } else {
      Object.keys(users).forEach((k) => ids.add(Number(k)));
    }
    return Array.from(ids)
      .filter((id) => Number.isFinite(id))
      .map((id) => ({ id, name: users[id]?.name || users[id]?.email || `Utilisateur #${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [memberships, users]);

  const filtered = useMemo(() => {
    let list = entries;
    if (filter !== 'all') list = list.filter((e) => e.kind === filter);
    if (workerId !== 'all') list = list.filter((e) => e.actorUserId === workerId);
    if (fromDate) {
      const min = dayStartIso(fromDate);
      list = list.filter((e) => (e.at || '') >= min);
    }
    if (toDate) {
      const max = dayEndIso(toDate);
      list = list.filter((e) => (e.at || '') <= max);
    }
    return list;
  }, [entries, filter, workerId, fromDate, toDate]);

  const counts = useMemo(() => ({
    total: entries.length,
    create: entries.filter((e) => e.kind === 'create').length,
    update: entries.filter((e) => e.kind === 'update').length,
    delete: entries.filter((e) => e.kind === 'delete').length,
  }), [entries]);

  const hasActiveFilters = filter !== 'all' || workerId !== 'all' || !!fromDate || !!toDate;

  const resetFilters = () => {
    setFilter('all');
    setWorkerId('all');
    setFromDate('');
    setToDate('');
  };

  const exportCsv = () => {
    const header = ['Date', 'Heure', 'Type', 'Auteur', 'Action', 'Détail'];
    const lines = filtered.map((e) => {
      const d = e.at ? new Date(e.at) : null;
      const date = d ? d.toLocaleDateString('fr-FR') : '';
      const time = d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
      return [
        date,
        time,
        KIND_META[e.kind].label,
        e.actor,
        e.title,
        e.detail || '',
      ].map(csvEscape).join(';');
    });
    const csv = `\uFEFF${[header.map(csvEscape).join(';'), ...lines].join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `journal-activite_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const timeAgo = (dateStr: string) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `il y a ${d} j`;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const chips: { key: 'all' | Kind; label: string; count: number }[] = [
    { key: 'all', label: 'Tout', count: counts.total },
    { key: 'create', label: 'Créations', count: counts.create },
    { key: 'update', label: 'Modifications', count: counts.update },
    { key: 'delete', label: 'Suppressions', count: counts.delete },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Tout ce que font les utilisateurs de votre équipe est enregistré ici — y compris les <strong className="text-foreground-700">suppressions</strong> (produits, services, commandes, catégories), avec l’auteur et l’horodatage.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-600 disabled:opacity-50 transition-colors"
            title="Exporter la sélection en CSV (Excel)"
          >
            <i className="ri-download-2-line"></i>
            Export CSV
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
        </div>
      </div>

      {/* Filtres : type + période + travailleur */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-3 mb-5 space-y-3">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-full sm:w-fit overflow-x-auto">
          {chips.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${filter === c.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              {c.label}
              <span className="text-[10px] px-1.5 rounded-full bg-background-200/70 text-foreground-600">{c.count}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-stretch sm:items-end">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-foreground-500">Du</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-foreground-500">Au</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            />
          </label>
          <label className="flex flex-col gap-1 sm:min-w-[200px]">
            <span className="text-[11px] font-medium text-foreground-500">Travailleur</span>
            <select
              value={workerId === 'all' ? 'all' : String(workerId)}
              onChange={(e) => setWorkerId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="all">Tous les travailleurs</option>
              {workerOptions.map((w) => (<option key={w.id} value={String(w.id)}>{w.name}</option>))}
            </select>
          </label>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i> Réinitialiser
            </button>
          )}
          <span className="text-xs text-foreground-500 sm:ml-auto self-center whitespace-nowrap">
            {filtered.length} entrée{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i></div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-history-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucune activité enregistrée pour ces filtres.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-200/60">
          {filtered.map((e) => (
            <div key={e.key} className="flex items-start gap-3 p-4">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${e.className}`}>
                <i className={`${e.icon} text-base`}></i>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${e.className}`}>
                    <i className={KIND_META[e.kind].icon}></i>{KIND_META[e.kind].label}
                  </span>
                  <span className="text-sm font-medium text-foreground-900 break-words">{e.title}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-user-3-line"></i>{e.actor}</span>
                  {e.detail && <span className="truncate">{e.detail}</span>}
                  <span className="inline-flex items-center gap-1 text-foreground-400"><i className="ri-time-line"></i>{timeAgo(e.at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}